import { FlexRow } from '@/components/common/Flex';
import { MotionBox } from '@/components/common/Motion';
import { FlexProps, Tooltip } from '@chakra-ui/react';
import { toPx } from '@common/util/styles';
import { Variants } from 'framer-motion';
import { hoverStyleProps } from './styleHelpers';

const SIZES = {
    md: {
        padding: 2,
        pillSize: 33,
        height: 33 + 2 * 2,
        width: 2 * 2 + 33 * 2,
    },
    sm: {
        padding: 1,
        pillSize: 20,
        height: 20 + 1 * 2,
        width: 1 * 2 + 20 * 2,
    },
};
type Size = keyof typeof SIZES;

const makeVariants = (size: Size): Variants => {
    const sizeProps = SIZES[size];
    return {
        LEFT: { x: sizeProps.padding },
        RIGHT: { x: sizeProps.padding + sizeProps.pillSize },
    };
};

export interface ToggleOption<V extends string> {
    value: V;
    el: React.ReactNode;
    label?: string;
    isDisabled?: boolean;
    selectedBg?: FlexProps['color'];
    unselectedColor?: FlexProps['color'];
}

interface ToggleProps<V extends string> extends Omit<FlexProps, 'transition'> {
    value: V;
    onChanged: (value: V) => void;
    options: [ToggleOption<V>, ToggleOption<V>];
    size?: 'sm' | 'md';
    showUnselectedIcon?: boolean;
}

const Toggle = <V extends string>({
    value,
    onChanged,
    options,
    size = 'md',
    showUnselectedIcon = true,
    ...props
}: ToggleProps<V>) => {
    const [leftOption, rightOption] = options;
    const selectedOption = options.find((o) => o.value === value);
    const unselectedOption = options.find((o) => o.value !== value);
    if (!selectedOption || !unselectedOption) return <></>;

    const sizeProps = SIZES[size];

    return (
        <FlexRow
            width={toPx(sizeProps.width)}
            height={toPx(sizeProps.height)}
            borderRadius={toPx(sizeProps.height)}
            p={toPx(sizeProps.padding)}
            align="stretch"
            justify="space-between"
            position="relative"
            transition="background 0.3s ease-in-out"
            bg={
                selectedOption.value === rightOption.value
                    ? rightOption.selectedBg ?? '#313D57'
                    : leftOption.selectedBg ?? 'gray.100'
            }
            userSelect="none"
            {...props}
        >
            {options.map((o) => {
                const isSelected = selectedOption.value === o.value;
                const opacity =
                    !isSelected && !showUnselectedIcon
                        ? 0
                        : o.isDisabled
                          ? 0.3
                          : isSelected
                            ? 1
                            : 0.7;
                const hoverOpacity = o.isDisabled ? 0.3 : 1;
                const color = isSelected
                    ? 'text'
                    : o.value === rightOption.value
                      ? rightOption.unselectedColor ?? 'textMuted'
                      : leftOption.unselectedColor ?? 'white';

                const content = (
                    <MotionBox
                        display="flex"
                        flexDirection={'row'}
                        flex={1}
                        justifyContent="center"
                        alignItems="center"
                        zIndex={10}
                        {...hoverStyleProps({ opacity: hoverOpacity })}
                        animate={{ opacity }}
                        // Known typing issue - no problem in operation.
                        // Details: https://chakra-ui.com/getting-started/with-framer#chakra-factory
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        transition={{ delay: 0.2, duration: 0.1 }}
                        color={color}
                        onClick={() => {
                            if (o.isDisabled) return;
                            onChanged(unselectedOption.value);
                        }}
                        key={o.value}
                    >
                        {isSelected ? o.el : showUnselectedIcon ? o.el : <></>}
                    </MotionBox>
                );

                return o.label ? (
                    <Tooltip key={o.value} placement="top" label={o.label}>
                        {content}
                    </Tooltip>
                ) : (
                    content
                );
            })}
            <MotionBox
                position="absolute"
                left={0}
                width={toPx(sizeProps.pillSize)}
                height={toPx(sizeProps.pillSize)}
                border="1px solid"
                borderColor="gray.200"
                bg="white"
                zIndex={1}
                shadow="sm"
                borderRadius={toPx(sizeProps.height)}
                variants={makeVariants(size)}
                animate={
                    selectedOption.value === rightOption.value
                        ? 'RIGHT'
                        : 'LEFT'
                }
                // Known typing issue - no problem in operation.
                // Details: https://chakra-ui.com/getting-started/with-framer#chakra-factory
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                transition={{ type: 'smooth', duration: 0.2 }}
                initial={false}
            />
        </FlexRow>
    );
};

export default Toggle;
